import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  Model3DData, AreaLines3D, AreaLinesIV7, PointSet_IV7,
  Vertices2Df, PointSet, PointSet_NX, AreaLinesNX } from '../../_objects/remeasurement';
import { ApiService } from '../base/api-base.service';
import { StereoMeasurementContractService } from '../interfaces/stereo-measurement-contract.service';
import { NetworkStatusService } from '../network-status.service';
import { StereoMeasurementImageOfflineService } from '../offline/stereo-measurement-offline.service';
import { StereoMeasurementImageOnlineService } from '../online/stereo-measurement-online.service';

@Injectable({
  providedIn: 'root'
})
export class StereoMeasurementService extends ApiService<StereoMeasurementContractService>
  implements StereoMeasurementContractService {

  constructor(
    protected networkStatusService: NetworkStatusService,
    protected offlineService: StereoMeasurementImageOfflineService,
    protected onlineService: StereoMeasurementImageOnlineService) {
    super(networkStatusService, offlineService, onlineService);
  }

  // SM01 POST /api/v1/stereo/reconstruct/{inspectionId}/{inspectionImageId}
  getAndGenerationOfMeasureInfo(inspectionId: string, inspectionImageId: string): Observable<any> {
    return this.service.getAndGenerationOfMeasureInfo(inspectionId, inspectionImageId);
  }

  // SM02 POST /api/v1/stereo/3dpointset/{inspectionId}/{inspectionImageId}
  measureDotCoordinate3dDataSet(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df, model: Model3DData): Observable<any> {
    return this.service.measureDotCoordinate3dDataSet(inspectionId, inspectionImageId, vertices2Df, model);
  }

  // SM03 POST /api/v1/stereo/3dpolyline/{inspectionId}/{inspectionImageId}
  getDistortionReproductionCurve3d(
    inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    return this.service.getDistortionReproductionCurve3d(inspectionId, inspectionImageId, vertices2Df, lineType, lr);
  }

  // SM04 POST /api/v1/stereo/3ddistance
  getTwoPointMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[]): Observable<any> {
    return this.service.getTwoPointMeasure3d(inspectionId, inspectionImageId, pointSets);
  }

  // SM05 POST /api/v1/stereo/3dpointtoline/{inspectionId}/{inspectionImageId}
  getLineReferenceMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[]): Observable<any> {
    return this.service.getLineReferenceMeasure3d(inspectionId, inspectionImageId, pointSets);
  }

  // SM06 POST /api/v1/stereo/3ddepth/{inspectionId}/{inspectionImageId}
  getSurfaceReferenceMeasure3d(inspectionId: string, inspectionImageId: string, pointSets: PointSet[], model: Model3DData): Observable<any> {
    return this.service.getSurfaceReferenceMeasure3d(inspectionId, inspectionImageId, pointSets, model);
  }

  // SM07 POST /api/v1/stereo/iv7pointset/{inspectionId}/{inspectionImageId}
  measureDotCoordinateIV7DataSet(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Observable<any> {
    return this.service.measureDotCoordinateIV7DataSet(inspectionId, inspectionImageId, pointSets);
  }

  // SM08 POST /api/v1/stereo/iv7polyline/{inspectionId}/{inspectionImageId}
  getDistortionReproductionCurveIV7(inspectionId: string, inspectionImageId: string,
    pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    return this.service.getDistortionReproductionCurveIV7(inspectionId, inspectionImageId, pointSets, lineType, lr);
  }

  // SM09 POST /api/v1/stereo/iv7distance/{inspectionId}/{inspectionImageId}
  getTwoPointMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    return this.service.getTwoPointMeasureIV7(inspectionId, inspectionImageId, pointSets);
  }

  // SM10 POST /api/v1/stereo/iv7pointtoline/{inspectionId}/{inspectionImageId}
  getLineReferenceMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    return this.service.getLineReferenceMeasureIV7(inspectionId, inspectionImageId, pointSets);
  }

  // SM11 POST /api/v1/stereo/iv7depth/{inspectionId}/{inspectionImageId}
  getSurfaceReferenceMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: PointSet_IV7[]): Observable<any> {
    return this.service.getSurfaceReferenceMeasureIV7(inspectionId, inspectionImageId, pointSets);
  }

  // SM12 POST /api/v1/stereo/3DareaLines/{inspectionId}/{inspectionImageId}
  getAreaTotalLengthMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLines3D): Observable<any> {
    return this.service.getAreaTotalLengthMeasure(inspectionId, inspectionImageId, pointSets);
  }

  // SM13 POST /api/v1/stereo/iv7area/{inspectionId}/{inspectionImageId}
  getAreaMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7): Observable<any> {
    return this.service.getAreaMeasure(inspectionId, inspectionImageId, pointSets);
  }

  // SM14 POST /api/v1/stereo/iv7lines/{inspectionId}/{inspectionImageId}
  getOverallLengthMeasure(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7): Observable<any> {
    return this.service.getOverallLengthMeasure(inspectionId, inspectionImageId, pointSets);
  }

  // SM16 POST /api/v1/stereo/nxpointset/{inspectionId}/{inspectionImageId}
  measureDotCoordinateNXDataSet(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Observable<any> {
    return this.service.measureDotCoordinateNXDataSet(inspectionId, inspectionImageId, pointSets);
  }

  // SM17 POST /api/v1/stereo/nxpolyline/{inspectionId}/{inspectionImageId}
  getDistortionReproductionCurveNX(inspectionId: string, inspectionImageId: string,
    pointSets: Vertices2Df[], lineType: number, lr: number): Observable<any> {
    return this.service.getDistortionReproductionCurveNX(inspectionId, inspectionImageId, pointSets, lineType, lr);
  }

  // SM18 POST /api/v1/stereo/nxdistance/{inspectionId}/{inspectionImageId}
  getTwoPointMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    return this.service.getTwoPointMeasureNX(inspectionId, inspectionImageId, pointSets);
  }

  // SM19 POST /api/v1/stereo/nxpointtoline/{inspectionId}/{inspectionImageId}
  getLineReferenceMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    return this.service.getLineReferenceMeasureNX(inspectionId, inspectionImageId, pointSets);
  }

  // SM20 POST /api/v1/stereo/nxdepth/{inspectionId}/{inspectionImageId}
  getSurfaceReferenceMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: PointSet_NX[]): Observable<any> {
    return this.service.getSurfaceReferenceMeasureNX(inspectionId, inspectionImageId, pointSets);
  }

  // SM21 POST /api/v1/stereo/nxarea/{inspectionId}/{inspectionImageId}
  getAreaMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX): Observable<any> {
    return this.service.getAreaMeasureNX(inspectionId, inspectionImageId, pointSets);
  }

  // SM22 POST /api/v1/stereo/nxlines/{inspectionId}/{inspectionImageId}
  getOverallLengthMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX): Observable<any> {
    return this.service.getOverallLengthMeasureNX(inspectionId, inspectionImageId, pointSets);
  }

  // SM15 GET /api/stereo/3dmodel/{inspectionId}/{inspectionImageId}
  get3DModelData(inspectionId: string, inspectionImageId: string): Observable<Model3DData> {
    return this.service.get3DModelData(inspectionId, inspectionImageId);
  }

  // SM GET /api/image/file
  // 3Dオブジェクトのテクスチャ画像取得
  async getImageBinaryUrl(inspectionId: string, inspectionImageId: string): Promise<string> {
    return this.service.getImageBinaryUrl(inspectionId, inspectionImageId);
  }

  // SM07 POST /api/v1/stereo/iv7pointset/{inspectionId}/{inspectionImageId}
  async measureDotCoordinateIV7DataSetAsync(inspectionId: string, inspectionImageId: string, pointSets: Vertices2Df): Promise<any> {
    return this.service.measureDotCoordinateIV7DataSetAsync(inspectionId, inspectionImageId, pointSets);
  }

  // SM02 POST /api/v1/stereo/3dpolyline/{inspectionId}/{inspectionImageId}
  async measureDotCoordinate3dDataSetAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df, model: Model3DData): Promise<any> {
    return this.service.measureDotCoordinate3dDataSetAsync(inspectionId, inspectionImageId, vertices2Df, model);
  }

  // SM16 POST /api/v1/stereo/nxpointset/{inspectionId}/{inspectionImageId}
  async measureDotCoordinateNXDataSetAsync(inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df): Promise<any> {
    return this.service.measureDotCoordinateNXDataSetAsync(inspectionId, inspectionImageId, vertices2Df);
  }

  // SM08 POST /api/v1/stereo/iv7polyline/{inspectionId}/{inspectionImageId}
  async getDistortionReproductionCurveIV7Async(inspectionId: string, inspectionImageId: string,
    pointSets: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    return this.service.getDistortionReproductionCurveIV7Async(inspectionId, inspectionImageId, pointSets, lineType, lr);
  }

  // SM03 POST /api/v1/stereo/nxpolyline/{inspectionId}/{inspectionImageId}
  async getDistortionReproductionCurve3dAsync(
    inspectionId: string, inspectionImageId: string, vertices2Df: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    return this.service.getDistortionReproductionCurve3dAsync(inspectionId, inspectionImageId, vertices2Df, lineType, lr);
  }

  // SM17 POST /api/v1/stereo/nxpolyline/{inspectionId}/{inspectionImageId}
  async getDistortionReproductionCurveNXAsync(inspectionId: string, inspectionImageId: string,
    vertices2Df: Vertices2Df[], lineType: number, lr: number): Promise<any> {
    return this.service.getDistortionReproductionCurveNXAsync(inspectionId, inspectionImageId, vertices2Df, lineType, lr);
  }

  measureMissingCorner(inspectionId: string, inspectionImageId: string, pointSets: PointSet[], model: Model3DData) {
    return this.service.measureMissingCorner(inspectionId, inspectionImageId, pointSets, model);
  }

}
